import React, { useEffect, useMemo, useState } from "react";
import { fetchJson } from "../../lib/api";
import { channelLabel, formatAmount } from "../../lib/format-utils";
import type { AdminOverviewResponse, AdminResendInboundResponse } from "../../lib/types";
import { useI18n } from "../../i18n/i18n";
import { Button } from "../button";
import { Card, CardContent, CardHeader, CardTitle } from "../card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../table";

const StatCard = ({ label, value, hint }: { label: string; value: string; hint?: string }) => (
  <Card>
    <CardHeader>
      <CardTitle className="text-sm font-medium text-muted">{label}</CardTitle>
    </CardHeader>
    <CardContent>
      <p className="text-2xl font-semibold text-ink">{value}</p>
      {hint ? <p className="mt-2 text-xs text-muted">{hint}</p> : null}
    </CardContent>
  </Card>
);

type AdminViewProps = {
  onHouseAccessClaimed?: () => Promise<void> | void;
};

export const AdminView = ({ onHouseAccessClaimed }: AdminViewProps) => {
  const { t, locale } = useI18n();
  const [overview, setOverview] = useState<AdminOverviewResponse | null>(null);
  const [inbound, setInbound] = useState<AdminResendInboundResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInboundLoading, setIsInboundLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inboundError, setInboundError] = useState<string | null>(null);
  const [reprocessingByEmailId, setReprocessingByEmailId] = useState<Record<string, boolean>>({});
  const [claimingByHouseRole, setClaimingByHouseRole] = useState<Record<string, boolean>>({});
  const dateLocale = locale === "da" ? "da-DK" : "en-US";
  const formatCount = (value: number) =>
    new Intl.NumberFormat(dateLocale, { maximumFractionDigits: 0 }).format(value);
  const formatDateTime = (value: string | null) => {
    if (!value) {
      return "—";
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return "—";
    }
    return new Intl.DateTimeFormat(dateLocale, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(parsed);
  };

  const loadOverview = () => {
    setIsLoading(true);
    setError(null);
    return fetchJson<AdminOverviewResponse>("/api/admin/overview")
      .then((data) => {
        setOverview(data);
      })
      .catch((loadError) => {
        setError(
          loadError instanceof Error
            ? loadError.message
            : t("admin.loadFailed", "Failed to load admin data"),
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const loadInbound = () => {
    setIsInboundLoading(true);
    setInboundError(null);
    return fetchJson<AdminResendInboundResponse>("/api/admin/resend-inbound?limit=50")
      .then((data) => {
        setInbound(data);
      })
      .catch((loadError) => {
        setInboundError(
          loadError instanceof Error
            ? loadError.message
            : t("admin.inboundLoadFailed", "Failed to load inbound emails"),
        );
      })
      .finally(() => {
        setIsInboundLoading(false);
      });
  };

  useEffect(() => {
    void Promise.all([loadOverview(), loadInbound()]);
  }, []);

  const houseNameById = useMemo(() => {
    if (!overview) {
      return new Map<number, string>();
    }
    return new Map(overview.houses.map((house) => [house.id, house.name]));
  }, [overview]);

  const latestJob = overview?.extras.latestImportJob ?? null;
  const latestJobHouse =
    latestJob && houseNameById.has(latestJob.houseId)
      ? houseNameById.get(latestJob.houseId)
      : null;
  const claimHouseAccess = async (houseId: number, role: "readonly" | "full") => {
    const claimKey = `${houseId}:${role}`;
    setClaimingByHouseRole((current) => ({
      ...current,
      [claimKey]: true,
    }));
    setError(null);
    try {
      await fetchJson(`/api/admin/houses/${houseId}/claim-access`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
        },
        body: JSON.stringify({ role }),
      });
      if (onHouseAccessClaimed) {
        await onHouseAccessClaimed();
      }
    } catch (claimError) {
      setError(
        claimError instanceof Error
          ? claimError.message
          : t("admin.claimAccessFailed", "Failed to claim house access"),
      );
    } finally {
      setClaimingByHouseRole((current) => ({
        ...current,
        [claimKey]: false,
      }));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-lg font-semibold text-ink">{t("admin.title", "Admin overview")}</p>
        <p className="text-sm text-muted">
          {t("admin.subtitle", "Owner-only snapshot of system activity.")}
        </p>
      </div>
      {isLoading ? (
        <p className="text-sm text-muted">{t("admin.loading", "Loading admin data...")}</p>
      ) : null}
      {error ? (
        <p className="text-sm text-error">
          {error === "forbidden" ? t("admin.accessDenied", "Access denied.") : error}
        </p>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label={t("admin.users", "Users")}
          value={overview ? formatCount(overview.totals.users) : "—"}
        />
        <StatCard
          label={t("admin.houses", "Houses")}
          value={overview ? formatCount(overview.totals.houses) : "—"}
          hint={
            overview
              ? t("admin.activeHouses", "{count} active", {
                  count: formatCount(overview.totals.activeHouses),
                })
              : undefined
          }
        />
        <StatCard
          label={t("admin.bookingValue", "Booking value {year}", {
            year: overview?.year ?? "",
          })}
          value={overview ? formatAmount(overview.totals.bookingsValueYtd, "DKK") : "—"}
        />
        <StatCard
          label={t("admin.housesMissingChannels", "Houses missing channels")}
          value={overview ? formatCount(overview.extras.housesMissingChannels) : "—"}
          hint={t("admin.noChannelCredentials", "No active channel credentials.")}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("admin.houses", "Houses")}</CardTitle>
          <p className="text-sm text-muted">
            {t("admin.housesSubtitle", "Channel connectivity and booking value.")}
          </p>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("admin.house", "House")}</TableHead>
                <TableHead>{t("admin.status", "Status")}</TableHead>
                <TableHead>{t("admin.channels", "Channels")}</TableHead>
                <TableHead className="text-right">
                  {t("admin.bookingValueYtd", "Booking value YTD")}
                </TableHead>
                <TableHead className="text-right">
                  {t("admin.access", "Access")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(overview?.houses ?? []).map((house) => (
                <TableRow key={house.id}>
                  <TableCell>{house.name}</TableCell>
                  <TableCell>
                    <span
                      className={
                        house.isActive ? "text-emerald-700" : "text-amber-700"
                      }
                    >
                      {house.isActive
                        ? t("admin.active", "Active")
                        : t("admin.inactive", "Inactive")}
                    </span>
                  </TableCell>
                  <TableCell>
                    {house.channelsConnected.length ? (
                      <div className="flex flex-wrap gap-2">
                        {house.channelsConnected.map((channelId) => (
                          <span
                            key={channelId}
                            className="rounded-full border px-2 py-0.5 text-xs text-muted"
                          >
                            {channelLabel(channelId)}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatAmount(house.bookingsValueYtd, "DKK")}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={claimingByHouseRole[`${house.id}:readonly`] ?? false}
                        onClick={async () => {
                          await claimHouseAccess(house.id, "readonly");
                        }}
                      >
                        {claimingByHouseRole[`${house.id}:readonly`]
                          ? t("admin.claimingReadonly", "Claiming...")
                          : t("admin.claimReadonly", "Claim readonly")}
                      </Button>
                      <Button
                        size="sm"
                        variant="default"
                        disabled={claimingByHouseRole[`${house.id}:full`] ?? false}
                        onClick={async () => {
                          await claimHouseAccess(house.id, "full");
                        }}
                      >
                        {claimingByHouseRole[`${house.id}:full`]
                          ? t("admin.claimingFull", "Claiming...")
                          : t("admin.claimFull", "Claim full")}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {overview && overview.houses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-muted">
                    {t("admin.noHouses", "No houses available.")}
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("admin.users", "Users")}</CardTitle>
          <p className="text-sm text-muted">
            {t("admin.usersSubtitle", "Account creation and login activity.")}
          </p>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("admin.email", "Email")}</TableHead>
                <TableHead>{t("admin.created", "Created")}</TableHead>
                <TableHead>{t("admin.lastLogin", "Last login")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(overview?.users ?? []).map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{formatDateTime(user.createdAt)}</TableCell>
                  <TableCell>{formatDateTime(user.lastLoginAt)}</TableCell>
                </TableRow>
              ))}
              {overview && overview.users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-muted">
                    {t("admin.noUsers", "No users available.")}
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t("admin.importActivity", "Import activity")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-sm text-muted">
              {overview
                ? t("admin.recentJobs", "{count} jobs in the last 7 days.", {
                    count: formatCount(overview.extras.recentImportJobCount),
                  })
                : "—"}
            </p>
            <div className="text-sm">
              {latestJob ? (
                <div className="space-y-1">
                  <p className="text-ink">
                    {latestJobHouse ? `${latestJobHouse} · ` : ""}
                    {channelLabel(latestJob.channel)} · {latestJob.kind}
                  </p>
                  <p className="text-muted">
                    {latestJob.status} · {formatDateTime(latestJob.startedAt)}
                  </p>
                  {latestJob.error ? (
                    <p className="text-xs text-error">{latestJob.error}</p>
                  ) : null}
                </div>
              ) : (
                <p className="text-muted">{t("admin.noImports", "No imports recorded yet.")}</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("admin.inboundEmails", "Inbound emails")}</CardTitle>
          <p className="text-sm text-muted">
            {t("admin.inboundEmailsSubtitle", "Recent Resend receiving events.")}
          </p>
        </CardHeader>
        <CardContent>
          {isInboundLoading ? (
            <p className="text-sm text-muted">{t("admin.loading", "Loading admin data...")}</p>
          ) : null}
          {inboundError ? (
            <p className="text-sm text-error">
              {inboundError === "forbidden"
                ? t("admin.accessDenied", "Access denied.")
                : inboundError}
            </p>
          ) : null}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("admin.inboundEmailId", "Email ID")}</TableHead>
                <TableHead>{t("admin.inboundStatus", "Status")}</TableHead>
                <TableHead>{t("admin.inboundReceived", "Received")}</TableHead>
                <TableHead>{t("admin.inboundHouse", "House")}</TableHead>
                <TableHead>{t("admin.inboundBooking", "Booking")}</TableHead>
                <TableHead>{t("admin.inboundFrom", "From")}</TableHead>
                <TableHead>{t("admin.inboundSubject", "Subject")}</TableHead>
                <TableHead>{t("admin.inboundError", "Error")}</TableHead>
                <TableHead>{t("admin.inboundActions", "Actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(inbound?.emails ?? []).map((email) => {
                const statusTone =
                  email.status === "processed"
                    ? "text-emerald-700"
                    : email.status === "error"
                      ? "text-error"
                      : "text-muted";
                const houseName =
                  email.houseId && houseNameById.has(email.houseId)
                    ? houseNameById.get(email.houseId)
                    : "—";
                const isReprocessing = reprocessingByEmailId[email.emailId] ?? false;
                return (
                  <TableRow key={email.id}>
                    <TableCell className="text-xs text-muted">{email.emailId}</TableCell>
                    <TableCell className={statusTone}>{email.status}</TableCell>
                    <TableCell>{formatDateTime(email.receivedAt ?? email.createdAt)}</TableCell>
                    <TableCell>{houseName}</TableCell>
                    <TableCell>{email.bookingId ?? "—"}</TableCell>
                    <TableCell>{email.fromEmail ?? "—"}</TableCell>
                    <TableCell>{email.subject ?? "—"}</TableCell>
                    <TableCell className="text-xs text-muted">
                      {email.error ?? "—"}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={isReprocessing}
                        onClick={async () => {
                          setReprocessingByEmailId((current) => ({
                            ...current,
                            [email.emailId]: true,
                          }));
                          try {
                            await fetchJson(
                              `/api/admin/resend-inbound/${email.emailId}/reprocess`,
                              { method: "POST" },
                            );
                            await loadInbound();
                          } catch (reprocessError) {
                            setInboundError(
                              reprocessError instanceof Error
                                ? reprocessError.message
                                : t(
                                    "admin.inboundReprocessFailed",
                                    "Failed to reprocess email",
                                  ),
                            );
                          } finally {
                            setReprocessingByEmailId((current) => ({
                              ...current,
                              [email.emailId]: false,
                            }));
                          }
                        }}
                      >
                        {isReprocessing
                          ? t("admin.inboundReprocessing", "Reprocessing...")
                          : t("admin.inboundReprocess", "Reprocess")}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {inbound && inbound.emails.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-muted">
                    {t("admin.inboundEmpty", "No inbound emails recorded yet.")}
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
